import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import './Requests.css';

const Requests = ({ onBack, token }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingId, setProcessingId] = useState(null);

  // Fetch requests from API
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        setError('');

        const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/getbyrequest`;
        console.log('Fetching requests from:', apiUrl);

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
        });

        const data = await response.json();
        console.log('Requests API Response:', data);

        if (response.ok && data.data) {
          const requestsArray = Array.isArray(data.data) ? data.data : [data.data];
          setRequests(requestsArray);
        } else if (response.ok && (!data.data || data.data.length === 0)) {
          setRequests([]);
        } else {
          setError(data.message || 'Failed to fetch requests');
        }
      } catch (err) {
        console.error('Error fetching requests:', err);
        setError(err.message || 'Failed to fetch requests');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchRequests();
    }
  }, [token]);

  const handleAccept = async (requestId) => {
    try {
      setProcessingId(requestId);

      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/accept/${requestId}`;
      console.log('Accepting request:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      const data = await response.json();
      console.log('Accept response:', data);

      if (response.ok) {
        Swal.fire({
          title: 'Accepted!',
          text: 'Request accepted successfully!',
          icon: 'success',
          confirmButtonColor: '#3085d6'
        });
        // Remove the request from the list
        setRequests(requests.filter(r => r.id !== requestId));
      } else {
        Swal.fire({
          title: 'Error',
          text: data.message || 'Failed to accept request',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error accepting request:', err);
      Swal.fire({
        title: 'Error',
        text: err.message || 'Failed to accept request',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You want to reject this request?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, reject it!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setProcessingId(requestId);

      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/reject/${requestId}`;
      console.log('Rejecting request:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      const data = await response.json();
      console.log('Reject response:', data);

      if (response.ok) {
        Swal.fire({
          title: 'Rejected!',
          text: 'Request rejected successfully!',
          icon: 'success',
          confirmButtonColor: '#3085d6'
        });
        // Remove the request from the list
        setRequests(requests.filter(r => r.id !== requestId));
      } else {
        Swal.fire({
          title: 'Error',
          text: data.message || 'Failed to reject request',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error rejecting request:', err);
      Swal.fire({
        title: 'Error',
        text: err.message || 'Failed to reject request',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className="requests-container">
      {/* Header */}
      <div className="requests-header">
        <button className="back-button" onClick={onBack}>
          ←
        </button>
        <h2>Requests</h2>
        <div style={{ width: '40px' }}></div>
      </div>

      {/* Content */}
      <div className="requests-content">
        {loading ? (
          <div className="loading-state">
            <p>Loading requests...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
            <button 
              onClick={() => window.location.reload()}
              style={{
                marginTop: '16px',
                padding: '8px 16px',
                backgroundColor: '#2c3e50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        ) : requests && requests.length > 0 ? (
          <div className="requests-list">
            {requests.map(request => (
              <div key={request.id} className="request-card">
                <div className="request-header">
                  <h3 className="request-title">
                    {request.title || request.case_title || 'Request'}
                  </h3>
                </div>

                <div className="request-details">
                  {request.description && (
                    <p className="request-description">{request.description}</p>
                  )}

                  {request.guest_user && (
                    <div className="request-user">
                      <span className="label">From:</span>
                      <span className="value">
                        {request.guest_user.first_name} {request.guest_user.last_name}
                      </span>
                    </div>
                  )}

                  {request.created_at && (
                    <div className="request-date">
                      <span className="label">Date:</span>
                      <span className="value">
                        {new Date(request.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                    </div>
                  )}

                  {request.status && (
                    <div className="request-status">
                      <span className="label">Status:</span>
                      <span className={`status-badge status-${request.status.toLowerCase()}`}>
                        {request.status}
                      </span>
                    </div>
                  )}
                </div>

                <div className="request-actions">
                  <button
                    className="accept-button"
                    onClick={() => handleAccept(request.id)}
                    disabled={processingId === request.id}
                    title="Accept request"
                  >
                    {processingId === request.id ? '⏳' : '✓ Accept'}
                  </button>
                  <button
                    className="reject-button"
                    onClick={() => handleReject(request.id)}
                    disabled={processingId === request.id}
                    title="Reject request"
                  >
                    {processingId === request.id ? '⏳' : '✕ Reject'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-requests">
            <p>No requests found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Requests;
